import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { UserRole } from 'src/auth/types';
import { UserRoleLabel } from 'src/constants';
import { CIVIL_LAW_NATURE_EMPLOYEES } from './const/roles';
import { ScheduleSubject } from '../Schedule/common/interfaces/ScheduleSubject.interface';

/**
 * Custom hook to manage the educational services in the Employees form
 *
 * @returns [selectedItems, filterChoices, setSelectedItems, handleRemoveService, sortOptions, groupByCategory]
 */
const useEducationalServices = (source: string) => {
    const [selectedItems, setSelectedItems] = useState<any[]>();
    const [subjects, setSubjects] = useState<ScheduleSubject[]>([]);
    const { getValues, setValue } = useFormContext();
    const [selectedRoles, setSelectedRoles] = useState(getValues(source)?.rolesCLNC);

    const getSelectedRolesArray = (selectedRoles: any) => {
        let selectedRolesArray: string[] = [];
        if (selectedRoles && selectedRoles.length > 0) {
            if (typeof selectedRoles === 'string') {
                selectedRolesArray = selectedRoles.split(',');
            } else {
                selectedRolesArray = selectedRoles;
            }
        }
        return selectedRolesArray;
    };

    const groupByCategory = (option: any) => {
        const categories = [];
        if (option.roles) {
            if (selectedRoles && selectedRoles.length > 0) {
                let selectedRolesArray = getSelectedRolesArray(selectedRoles);

                option.roles.forEach((role: UserRole) => {
                    const selectedRoleExists = selectedRolesArray.some(
                        (selRole: string) => selRole.toLowerCase() === role.toLowerCase()
                    );
                    if (selectedRoleExists) categories.push(UserRoleLabel[role]);

                    return selectedRoleExists;
                });
            } else {
                option.roles.forEach((role: UserRole) => categories.push(UserRoleLabel[role]));
            }
        }
        if (categories.length === 0) categories.push('Інші');
        return categories.join(', ');
    };

    const sortOptions = (a: any, b: any) => {
        const groupA = groupByCategory(a);
        const groupB = groupByCategory(b);

        const isOtherA = groupA.includes('Інші');
        const isOtherB = groupB.includes('Інші');

        if (isOtherA && !isOtherB) return 1;
        if (!isOtherA && isOtherB) return -1;

        if (groupA < groupB) return -1;
        if (groupA > groupB) return 1;

        // If groups are equal, sort by name
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        return 0;
    };

    const handleRemoveService = (source: string, itemId: any) => {
        if (selectedItems) {
            setValue(
                source,
                getValues(source).filter((item: any) => item !== itemId)
            );
            setSelectedItems(selectedItems?.filter((item) => item.specialId !== itemId));
        }
    };

    useEffect(() => {
        setSelectedRoles(getValues(source)?.rolesCLNC);
    }, [getValues(source)?.rolesCLNC]);

    useEffect(() => {
        const selectedValues = getValues(source)?.subjectIds;
        const selectedSubjects = subjects?.filter((x) => selectedValues.includes(x.id));
        setSelectedItems(selectedSubjects);
    }, [subjects]);

    const filterChoices = (choice: any) => {
        if (selectedRoles && selectedRoles.length > 0) {
            let selectedRolesArray = getSelectedRolesArray(selectedRoles);

            return choice.roles?.some((role: string) => {
                const selectedRoleExists = selectedRolesArray.some(
                    (selRole: string) => selRole.toLowerCase() === role.toLowerCase()
                );
                const civilLawRoleExists = CIVIL_LAW_NATURE_EMPLOYEES.some(
                    (civilLawRole: string) => civilLawRole.toLowerCase() === role.toLowerCase()
                );

                return selectedRoleExists && civilLawRoleExists;
            });
        }
        return choice.roles?.some((role: any) => CIVIL_LAW_NATURE_EMPLOYEES.includes(role));
    };

    return {
        selectedItems,
        filterChoices,
        setSelectedItems,
        handleRemoveService,
        sortOptions,
        groupByCategory,
        setSubjects
    };
};

export default useEducationalServices;
